import { memo, useMemo } from 'react';
import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import { FormattedMessage } from 'react-intl';
import { Navigate, useLocation } from 'react-router-dom';
import getCurrentCategoryTree from '../../../../core/util/getCurrentCategoryTree';
import ChevronLeftIcon from '../../../../core/components/icons/ChevronLeftIcon';
import { FluidProgressIndicator } from '../../../../core/components/ProgressIndicators';
import CollapsibleContent from '../../../../core/components/CollapsibleContent';
import useCategory from '../../../../core/queries/useCategory';
import type Category from '../../../../core/types/category';
import findInTreeNodes from '../../../../core/util/findInTreeNodes';
import CustomLink from '../../../../core/components/CustomLink';
import withErrorBoundary from '../../../../core/components/errors/withErrorBoundary';
import styles from './CategoriesTree.module.scss';
import CollapsibleSideBarFilterTitle from './CollapsibleSideBarFilterTitle';
import CategoryList from './CategoryList';
const RedirectToRootCategoryPage = memo(
    ({ rootCategoryId }: { rootCategoryId: string }) => {
        const { search } = useLocation();
        return <Navigate to={`../categories/${rootCategoryId}${search}`} />;
    }
);

const CategoryTreeWithSelectedCategories = withErrorBoundary(
    memo(
        ({
            paramIgnoreList = [],
            tree,
            countsLoading,
            singleRootCategory,
        }: {
            paramIgnoreList?: string[];
            tree: Category[];
            countsLoading: boolean;
            singleRootCategory?: boolean;
        }) => {
            const { categoryId = '' } = useCategory();
            const { search } = useLocation();

            const selectedCategory = useMemo(() => {
                return findInTreeNodes(tree, node => node.id === categoryId);
            }, [tree, categoryId]);

            const categoryAncestryIds = useMemo(() => {
                return selectedCategory
                    ? selectedCategory.path.map(({ id }) => id)
                    : [];
            }, [selectedCategory]);

            const currentCategories = useMemo(() => {
                return categoryAncestryIds.length === 0
                    ? tree
                    : (getCurrentCategoryTree(tree, categoryId) ?? []);
            }, [categoryAncestryIds, tree, categoryId]);

            return (
                <>
                    {tree.length > 1 &&
                        categoryAncestryIds.length >= 1 &&
                        !singleRootCategory && (
                            <div className={styles.categoryItem}>
                                <CustomLink
                                    role="button"
                                    tabIndex={0}
                                    data-test-class="categoryMenuItem"
                                    className={clsx(
                                        'category-link',
                                        styles.categoryItemLink
                                    )}
                                    to={`../${search}`}
                                >
                                    <div
                                        className={clsx(
                                            styles.nav,
                                            styles.parent
                                        )}
                                    >
                                        <span className={styles.backIcon}>
                                            <ChevronLeftIcon />
                                        </span>
                                        <FormattedMessage id="all categories" />
                                    </div>
                                </CustomLink>
                            </div>
                        )}
                    <CategoryList
                        activeCategoryId={categoryId}
                        paramIgnoreList={paramIgnoreList}
                        countsLoading={countsLoading}
                        items={currentCategories}
                        categoryAncestryIds={categoryAncestryIds}
                    />
                </>
            );
        }
    )
);

const SearchResultsCategoriesTreeWrapper = memo(
    ({ children }: { children: ReactNode }) => {
        return (
            <CollapsibleContent
                expanded
                smallIcon
                filterIcon
                title={
                    <CollapsibleSideBarFilterTitle>
                        <FormattedMessage id="category" />
                    </CollapsibleSideBarFilterTitle>
                }
                endArrow
            >
                {children}
            </CollapsibleContent>
        );
    }
);

const CategoriesTree = ({
    paramIgnoreList = [],
    notRedirectToRoot,
    singleRootCategory,
    countsLoading,
    categoryId,
    loading,
    tree,
}: {
    paramIgnoreList?: string[];
    notRedirectToRoot?: boolean;
    singleRootCategory?: boolean;
    countsLoading?: boolean;
    loading?: boolean;
    categoryId?: string;
    tree: Category[];
}) => {
    if (loading && tree.length === 0) {
        return <FluidProgressIndicator />;
    }

    if (tree.length === 0) {
        return null;
    }

    if (tree.length === 1 && !categoryId && !notRedirectToRoot) {
        return <RedirectToRootCategoryPage rootCategoryId={tree[0].id} />;
    }

    return (
        <SearchResultsCategoriesTreeWrapper>
            <CategoryTreeWithSelectedCategories
                paramIgnoreList={paramIgnoreList}
                tree={tree}
                countsLoading={countsLoading || false}
                singleRootCategory={singleRootCategory}
            />
        </SearchResultsCategoriesTreeWrapper>
    );
};

export default memo(withErrorBoundary(CategoriesTree));
